import { BrowserRouter, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { AuthProvider, roleLabel, useAuth } from "./lib/AuthContext";
import type { UserRole } from "./lib/authApi";
import { Login } from "./views/Login";
import { ChangePassword } from "./views/ChangePassword";
import { Users } from "./views/Users";
import { Dashboard } from "./views/Dashboard";
import { ConnectBank } from "./views/ConnectBank";
import { Payloads } from "./views/Payloads";
import { Credentials } from "./views/Credentials";
import { Companies } from "./views/Companies";
import { Accounts } from "./views/Accounts";
import { AccountMovements } from "./views/AccountMovements";
import { AccountSapEntries } from "./views/AccountSapEntries";
import { AccountReconciliation } from "./views/AccountReconciliation";
import { Loans } from "./views/Loans";
import { LoanAmortization } from "./views/LoanAmortization";
import { Leasings } from "./views/Leasings";
import { LeasingSchedule } from "./views/LeasingSchedule";
import { AvalLines } from "./views/AvalLines";
import { AvalLineDetail } from "./views/AvalLineDetail";
import { AvalSchedule } from "./views/AvalSchedule";
import { UnmatchedView } from "./views/Unmatched";
import { CompaniesAggregated } from "./views/CompaniesAggregated";
import { Sap } from "./views/Sap";
import { SapErrors } from "./views/SapErrors";
import { OrphanBoes } from "./views/OrphanBoes";
import { PostingRulesView } from "./views/PostingRules";
import { PrintSummary } from "./views/PrintSummary";
import { Forecast } from "./views/Forecast";
import { RecurringItems } from "./views/RecurringItems";
import { PaymentBatches } from "./views/PaymentBatches";
import { ConfirmingBatches } from "./views/ConfirmingBatches";
import { CollectionBatches } from "./views/CollectionBatches";
import { Cartera } from "./views/Cartera";

/**
 * Cada entrada del NAV puede exigir uno o más roles. Un admin ve todo
 * (el helper `hasRole` ya lo trata). Si `roles` está vacío, la ve cualquier
 * usuario autenticado.
 */
type NavItem = {
  to: string;
  label: string;
  icon: JSX.Element;
  end?: boolean;
  /** Apartado que da acceso (permisos por usuario). Sin él: solo admin. */
  section?: string;
  roles?: UserRole[];
};

const NAV: NavItem[] = [
  { section: "dashboard", to: "/", end: true, label: "Dashboard", icon: <IconDashboard /> },
  { section: "resumen", to: "/resumen", label: "Resumen empresas", icon: <IconChart /> },
  { section: "prevision", to: "/prevision", label: "Previsión tesorería", icon: <IconTrend /> },
  { section: "recurrentes", to: "/conceptos-recurrentes", label: "Recurrentes", icon: <IconRepeat /> },
  { section: "empresas", to: "/empresas", label: "Empresas", icon: <IconBuilding /> },
  { section: "cuentas", to: "/cuentas", label: "Cuentas", icon: <IconWallet /> },
  { section: "prestamos", to: "/prestamos", label: "Préstamos", icon: <IconBank /> },
  { section: "leasings", to: "/leasings", label: "Leasings", icon: <IconLeasing /> },
  { section: "avales", to: "/avales", label: "Avales", icon: <IconAval /> },
  { section: "pagos-proveedor", to: "/pagos-proveedor", label: "Pagos proveedor", icon: <IconSepa /> },
  { section: "pagos-confirming", to: "/pagos-confirming", label: "Confirming", icon: <IconConfirming /> },
  { section: "cobros-domiciliacion", to: "/cobros-domiciliacion", label: "Cobros domiciliación", icon: <IconCollection /> },
  { section: "cartera", to: "/cartera", label: "Cartera y vtos.", icon: <IconCollection /> },
  { section: "sin-vincular", to: "/sin-vincular", label: "Sin vincular", icon: <IconUnlink /> },
  { section: "conectar", to: "/conectar", label: "Conectar banco", icon: <IconPlug /> },
  { section: "credenciales", to: "/credenciales", label: "Credenciales", icon: <IconKey /> },
  { section: "payloads", to: "/payloads", label: "Payloads", icon: <IconCode /> },
  { section: "sap", to: "/sap", label: "SAP", icon: <IconServer /> },
  { section: "reglas", to: "/reglas-contabilizacion",
    label: "Reglas contab.",
    icon: <IconRules />,
  },
  { section: "errores-sap", to: "/errores-sap", label: "Errores SAP", icon: <IconAlert /> },
  { section: "efectos-huerfanos", to: "/efectos-huerfanos", label: "Efectos huérfanos", icon: <IconAlert /> },
  { to: "/usuarios", label: "Usuarios", icon: <IconUsers />, roles: ["admin"] },
];

export default function App(): JSX.Element {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

/**
 * Decide qué pintar según el estado de sesión:
 *   - loading → nada (pantalla en blanco muy breve mientras arranca /me)
 *   - unauthenticated → <Login />
 *   - authenticated + mustChangePassword → <ChangePassword forced />
 *   - authenticated → el shell normal con router
 */
function AuthGate(): JSX.Element | null {
  const { status, mustChangePassword } = useAuth();
  if (status === "loading") return null;
  if (status === "unauthenticated") return <Login />;
  if (mustChangePassword) return <ChangePassword forced />;
  return <Shell />;
}

function Shell(): JSX.Element {
  const { user, logout, hasRole, hasSection } = useAuth();
  const visibleNav = NAV.filter((item) =>
    item.section ? hasSection(item.section) : !item.roles || hasRole(...item.roles),
  );
  return (
    <BrowserRouter>
      <div className="shell">
        <aside className="sidebar">
          <div className="brand">
            <div className="brand-mark">EC</div>
            <div className="brand-text">
              <div className="brand-name">ConciliaEcilimp</div>
              <div className="brand-tag">Tesorería · Conciliación</div>
            </div>
          </div>
          <nav className="side-nav">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => "side-link" + (isActive ? " active" : "")}
              >
                <span className="side-icon">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="side-foot">
            {user && (
              <div className="user-chip">
                <div className="user-chip-name">{user.displayName}</div>
                <div className="user-chip-role">{roleLabel(user.role)}</div>
                <button
                  type="button"
                  className="user-chip-logout"
                  onClick={() => void logout()}
                  title="Cerrar sesión"
                >
                  Salir
                </button>
              </div>
            )}
            <div className="foot-line">v0.1 · Ecilimp</div>
          </div>
        </aside>

        <main className="main">
          <Routes>
            {/* La home "/" es el Dashboard para admin; para contable/consulta
                redirigimos a "Resumen empresas" que es la vista más útil. */}
            <Route path="/" element={<HomeForRole />} />
            <Route path="/resumen" element={<SectionGuarded section="resumen"><CompaniesAggregated /></SectionGuarded>} />
            <Route path="/resumen/imprimir" element={<PrintSummary />} />
            <Route path="/prevision" element={<SectionGuarded section="prevision"><Forecast /></SectionGuarded>} />
            <Route path="/conceptos-recurrentes" element={<SectionGuarded section="recurrentes"><RecurringItems /></SectionGuarded>} />
            <Route path="/empresas" element={<SectionGuarded section="empresas"><Companies /></SectionGuarded>} />
            <Route
              path="/cuentas"
              element={<SectionGuarded section="cuentas"><Accounts /></SectionGuarded>}
            />
            {/* Las sub-rutas de una cuenta concreta se dejan accesibles a
                contable — puede llegar a ellas desde "Resumen empresas" o
                enlaces cruzados aunque no vea el listado maestro. */}
            <Route path="/cuentas/:iban/movimientos" element={<AccountMovements />} />
            <Route path="/cuentas/:iban/apuntes-sap" element={<AccountSapEntries />} />
            <Route path="/cuentas/:iban/conciliar" element={<AccountReconciliation />} />
            <Route path="/prestamos" element={<SectionGuarded section="prestamos"><Loans /></SectionGuarded>} />
            <Route
              path="/prestamos/:bankCode/:contractNumber/amortizacion"
              element={<LoanAmortization />}
            />
            <Route path="/leasings" element={<SectionGuarded section="leasings"><Leasings /></SectionGuarded>} />
            <Route
              path="/leasings/:bankCode/:contractNumber/cuadro"
              element={<LeasingSchedule />}
            />
            <Route path="/avales" element={<SectionGuarded section="avales"><AvalLines /></SectionGuarded>} />
            <Route path="/avales/:bankCode/:lineNumber" element={<AvalLineDetail />} />
            <Route
              path="/avales/:bankCode/:lineNumber/:avalNumber/cuadro"
              element={<AvalSchedule />}
            />
            <Route
              path="/pagos-proveedor"
              element={<SectionGuarded section="pagos-proveedor"><PaymentBatches /></SectionGuarded>}
            />
            <Route
              path="/pagos-confirming"
              element={<SectionGuarded section="pagos-confirming"><ConfirmingBatches /></SectionGuarded>}
            />
            <Route
              path="/cobros-domiciliacion"
              element={<SectionGuarded section="cobros-domiciliacion"><CollectionBatches /></SectionGuarded>}
            />
            <Route
              path="/cartera"
              element={<SectionGuarded section="cartera"><Cartera /></SectionGuarded>}
            />
            <Route
              path="/sin-vincular"
              element={<SectionGuarded section="sin-vincular"><UnmatchedView /></SectionGuarded>}
            />
            <Route
              path="/conectar"
              element={<SectionGuarded section="conectar"><ConnectBank /></SectionGuarded>}
            />
            <Route
              path="/credenciales"
              element={<SectionGuarded section="credenciales"><Credentials /></SectionGuarded>}
            />
            <Route
              path="/payloads"
              element={<SectionGuarded section="payloads"><Payloads /></SectionGuarded>}
            />
            <Route
              path="/sap"
              element={<SectionGuarded section="sap"><Sap /></SectionGuarded>}
            />
            <Route
              path="/reglas-contabilizacion"
              element={<SectionGuarded section="reglas"><PostingRulesView /></SectionGuarded>}
            />
            <Route
              path="/reglas-contabilizacion/:bankCode"
              element={<SectionGuarded section="reglas"><PostingRulesView /></SectionGuarded>}
            />
            <Route
              path="/errores-sap"
              element={<SectionGuarded section="errores-sap"><SapErrors /></SectionGuarded>}
            />
            <Route
              path="/efectos-huerfanos"
              element={<SectionGuarded section="efectos-huerfanos"><OrphanBoes /></SectionGuarded>}
            />
            <Route
              path="/usuarios"
              element={<RoleGuarded roles={["admin"]}><Users /></RoleGuarded>}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

function SectionGuarded({
  section,
  children,
}: {
  section: string;
  children: JSX.Element;
}): JSX.Element {
  const { hasSection } = useAuth();
  if (!hasSection(section)) {
    // Si tiene algún otro apartado, lo llevamos al primero en vez de dejarle en un callejón.
    const first = NAV.find((i) => i.section && hasSection(i.section));
    if (first) return <Navigate to={first.to} replace />;
    return (
      <section className="card">
        <h2>Sin permiso</h2>
        <p>No tienes acceso a este apartado. Solicítalo a un administrador.</p>
      </section>
    );
  }
  return children;
}

function RoleGuarded({
  roles,
  children,
}: {
  roles: UserRole[];
  children: JSX.Element;
}): JSX.Element {
  const { hasRole } = useAuth();
  if (!hasRole(...roles)) {
    return (
      <section className="card">
        <h2>Sin permiso</h2>
        <p>Tu perfil no tiene acceso a esta sección.</p>
      </section>
    );
  }
  return children;
}

/** Ruta "/": Dashboard para admin, Resumen empresas para el resto. */
function HomeForRole(): JSX.Element {
  const { hasSection } = useAuth();
  if (hasSection("dashboard")) return <Dashboard />;
  const first = NAV.find((i) => i.section && hasSection(i.section));
  return <Navigate to={first?.to ?? "/resumen"} replace />;
}

function NotFound(): JSX.Element {
  return (
    <section className="card">
      <h2>Página no encontrada</h2>
      <p>La ruta solicitada no existe.</p>
    </section>
  );
}

function Svg({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function IconDashboard() {
  return (
    <Svg>
      <rect x="3" y="3" width="7" height="9" rx="1.2" />
      <rect x="14" y="3" width="7" height="5" rx="1.2" />
      <rect x="14" y="12" width="7" height="9" rx="1.2" />
      <rect x="3" y="16" width="7" height="5" rx="1.2" />
    </Svg>
  );
}
function IconChart() {
  return (
    <Svg>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20H2" />
    </Svg>
  );
}
function IconBuilding() {
  return (
    <Svg>
      <rect x="4" y="3" width="16" height="18" rx="1.2" />
      <path d="M9 7h.01M9 11h.01M9 15h.01M15 7h.01M15 11h.01M15 15h.01" />
      <path d="M10 21v-3h4v3" />
    </Svg>
  );
}
function IconWallet() {
  return (
    <Svg>
      <path d="M3 7a2 2 0 0 1 2-2h13v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M18 12h3v4h-3a2 2 0 0 1 0-4Z" />
    </Svg>
  );
}
function IconBank() {
  return (
    <Svg>
      <path d="M3 10 12 4l9 6" />
      <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
      <path d="M3 20h18" />
    </Svg>
  );
}
function IconLeasing() {
  return (
    <Svg>
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </Svg>
  );
}
function IconAval() {
  return (
    <Svg>
      <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}
function IconUnlink() {
  return (
    <Svg>
      <path d="m8 5-3 3a4 4 0 0 0 5.66 5.66L12 12" />
      <path d="m16 19 3-3a4 4 0 0 0-5.66-5.66L12 12" />
      <path d="M4 4l2 2M20 20l-2-2" />
    </Svg>
  );
}
function IconPlug() {
  return (
    <Svg>
      <path d="M9 2v6M15 2v6" />
      <path d="M6 8h12v4a6 6 0 0 1-12 0Z" />
      <path d="M12 18v4" />
    </Svg>
  );
}
function IconKey() {
  return (
    <Svg>
      <circle cx="8" cy="15" r="4" />
      <path d="m10.8 12.2 9.2-9.2" />
      <path d="m18 5 2 2" />
      <path d="m15 8 2 2" />
    </Svg>
  );
}
function IconCode() {
  return (
    <Svg>
      <path d="m8 8-5 4 5 4" />
      <path d="m16 8 5 4-5 4" />
      <path d="m14 4-4 16" />
    </Svg>
  );
}
function IconServer() {
  return (
    <Svg>
      <rect x="3" y="4" width="18" height="7" rx="1.2" />
      <rect x="3" y="13" width="18" height="7" rx="1.2" />
      <path d="M7 7.5h.01M7 16.5h.01" />
    </Svg>
  );
}
function IconAlert() {
  return (
    <Svg>
      <path d="M12 3 2 20h20L12 3z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </Svg>
  );
}
function IconRules() {
  return (
    <Svg>
      <path d="M4 6h10" />
      <path d="M4 12h10" />
      <path d="M4 18h10" />
      <circle cx="18" cy="6" r="1.5" />
      <circle cx="18" cy="12" r="1.5" />
      <circle cx="18" cy="18" r="1.5" />
    </Svg>
  );
}
function IconTrend() {
  return (
    <Svg>
      <path d="M3 17 9 11l4 4 8-8" />
      <path d="M14 7h7v7" />
    </Svg>
  );
}
function IconRepeat() {
  return (
    <Svg>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </Svg>
  );
}
function IconUsers() {
  return (
    <Svg>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 21c0-3.87 3.13-7 7-7s7 3.13 7 7" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M22 21c0-3.03-2.24-5.5-5-5.5" />
    </Svg>
  );
}
function IconSepa() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h4" />
      <path d="M15 15h2" />
    </Svg>
  );
}
function IconConfirming() {
  return (
    <Svg>
      <path d="M4 4h12l4 4v12H4z" />
      <path d="M16 4v4h4" />
      <path d="M8 14h8" />
      <path d="M8 18h5" />
    </Svg>
  );
}
function IconCollection() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2 4-4" />
    </Svg>
  );
}
